import api from "@/lib/api";

// 2026-07-29 · 512 KB → 2 MB. Production gateways typically accept ≤ 4 MB;
// 2 MB stays well below that ceiling while cutting the RTT count 4× — a big
// win for China users whose per-request latency to the K8s ingress is
// 200-500 ms.
const CHUNK_SIZE = 2 * 1024 * 1024;

// Send up to CONCURRENCY chunks in parallel. Sequential upload was the
// single biggest contributor to slow China uploads: with 200 ms RTT and
// 5 chunks, plain sequential burns 1 s of pure wait time before the first
// byte of the last chunk goes out. Parallel 3-way brings that to ~0.4 s.
const CONCURRENCY = 3;

/**
 * Upload a File in chunks (parallel bounded). Returns the ``upload_id`` you
 * pass to the target API's finalize endpoint (e.g. POST /products/upload-image).
 *
 * @param {File|Blob} file
 * @param {(pct:number)=>void} [onProgress]
 * @returns {Promise<string>} the upload id
 */
export async function uploadInChunks(file, onProgress) {
  const uploadId = crypto.randomUUID();
  const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  let completed = 0;

  // Simple bounded-concurrency worker pool. Each worker pulls the next chunk
  // index off a shared cursor until we've fired every chunk. We DON'T need
  // ordering guarantees — the backend indexes chunks by ``chunk_index`` in
  // MongoDB and reassembles in sorted order at finalize time.
  let cursor = 0;
  async function worker() {
    for (;;) {
      const i = cursor++;
      if (i >= total) return;
      const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const form = new FormData();
      form.append("upload_id", uploadId);
      form.append("chunk_index", String(i));
      form.append("total_chunks", String(total));
      form.append("chunk", blob, file.name || "chunk");
      await api.post("/files/chunk", form, { timeout: 60000 });
      completed += 1;
      onProgress?.(Math.round((completed / total) * 100));
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
  return uploadId;
}
